/* eslint-disable react-refresh/only-export-components --
   This file colocates the route config (default export) with the
   layout components used only by those routes (RootLayout,
   ProtectedLayout, SaaSAnalyticsTracker), so the route definition reads
   in one place. */
// frontend/src/App.jsx
//
// Route configuration for createBrowserRouter (data router).
// Exports appRoutes consumed by main.jsx.
//
// Pages of the tabbed shell are imported by tabs/routeRegistry; only routes
// outside the shell (login, password reset, etc.) are imported here.

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import AppLayout from './components/layout/AppLayout';
import Login from './components/login/Login';
import SetupAccount from './pages/SetupAccount';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConfirmEmail from './pages/ConfirmEmail';
import './i18n/i18n.jsx';

import { AllEnterpriseModule, provideGlobalGridOptions } from "ag-grid-enterprise";
import { AgGridProvider } from "ag-grid-react";
// Side-effect import: registers AG Charts modules and applies the license.
// AG_LICENSE_KEY is re-exported for the AgGridProvider below — AG Grid takes
// the license via the provider rather than the static LicenseManager.
import { AG_LICENSE_KEY } from './config/agLicense';
import { GRID_TOOLTIP_OPTIONS } from './utils/gridTooltip';
import { GRID_LOCALE_OPTIONS } from './utils/gridLocale';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { extractTenantSlug, apiRequest } from './services/SessionApiService';
import { BillingStatusContext } from './contexts/BillingStatusContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import PricingGate from './components/billing/PricingGate';
import { ENTRY_SCREEN, entryScreen } from './components/entry/entryScreen';
import EntryBackdrop from './components/entry/EntryBackdrop';
import EntryLoading from './components/entry/EntryLoading';
import EntryErrorScreen from './components/entry/EntryErrorScreen';
import LeaveForShopifyDoor from './components/entry/LeaveForShopifyDoor';
import StoreClaimQuestion from './components/entry/StoreClaimQuestion';
import StoreSetup from './components/entry/StoreSetup';
import SyncScreen from './components/sync/SyncScreen';
import { useEntryStatus } from './hooks/useEntryStatus';
import { useSyncStatus } from './hooks/useSyncStatus';
import { useTracking } from './hooks/useTracking';
import { TabsProvider } from './tabs/TabsProvider';
import TabsHost from './tabs/TabsHost';
import CloseTabsOnStoreChange from './tabs/CloseTabsOnStoreChange';
import AppToast from './components/common/AppToast';
import ReloadOnNewVersion from './components/common/ReloadOnNewVersion';
import { REGISTERED_ROUTES, findRouteForPath } from './tabs/routeRegistry';


const AG_GRID_MODULES = [AllEnterpriseModule];
// Every grid's hints take the app's one shape (utils/gridTooltip.js), and AG
// Grid's own words come in the merchant's language (utils/gridLocale.js).
provideGlobalGridOptions({ ...GRID_TOOLTIP_OPTIONS, ...GRID_LOCALE_OPTIONS });

/**
 * Analytics Tracker for SaaS dashboard.
 * Uses apiRequest which handles CSRF, credentials, and store context.
 *
 * `recordsPage` is false in front of an entry screen: the URL there names a
 * page the merchant cannot see, so no page view is recorded — but the queue
 * still flows, so a click made on the screen (Contact sales on the paywall)
 * is delivered.
 */
function SaaSAnalyticsTracker({ recordsPage = true }) {
  const trackingEndpoint = '/api/analytics/track/';

  const fetchFn = useCallback(async (_url, options) => {
    return apiRequest(trackingEndpoint, options);
  }, []);

  // Record the matched route *pattern* (e.g. /purchase-orders/:id),
  // not the raw path — validates against the registry (unknown → null,
  // which the hook skips) and keeps parametric routes from exploding page
  // cardinality. Strip react-router's optional-param marker ('?') so the
  // stored pattern stays a clean path (e.g.
  // /suppliers/:id/catalog/:itemFilter).
  const { pathname } = useLocation();
  const routePath = findRouteForPath(pathname)?.path?.replace(/\?/g, '') ?? null;

  useTracking({ fetchFn, apiUrl: trackingEndpoint, path: recordsPage ? routePath : null });
  return null;
}

/**
 * The popup for an entry screen other than APP (`entryScreen.js`), drawn by
 * ProtectedLayout in front of the inert app frame (`EntryBackdrop`).
 */
function EntryCard({ screen, entry, sync }) {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();

  if (screen === ENTRY_SCREEN.LOADING) {
    return <EntryLoading />;
  }

  if (screen === ENTRY_SCREEN.CLAIM_ERROR) {
    return <EntryErrorScreen message={t('entry.claimReadFailed')} error={entry.readError} onRetry={entry.retry} />;
  }

  if (screen === ENTRY_SCREEN.QUESTION) {
    return (
      <StoreClaimQuestion
        question={entry.claim.question}
        onAnswer={entry.answerClaim}
        onReread={entry.retry}
      />
    );
  }

  if (screen === ENTRY_SCREEN.SETUP_ERROR) {
    return <EntryErrorScreen message={t('entry.setupReadFailed')} onRetry={entry.retry} />;
  }

  if (screen === ENTRY_SCREEN.SETUP) {
    return (
      <StoreSetup
        step={entry.setup.step}
        onAnswer={entry.answerSetup}
        onAnswered={entry.retry}
      />
    );
  }

  // The import a denied merchant's price waits on: no way out — the paywall
  // follows by itself the moment the order history is in.
  if (screen === ENTRY_SCREEN.PRICE_IMPORT) {
    return (
      <SyncScreen
        syncStatus={sync.syncStatus}
        reconnectUrl={selectedStore.shopify_sign_in_url}
      />
    );
  }

  if (screen === ENTRY_SCREEN.PAYWALL) {
    return (
      <PricingGate
        billingStatus={entry.billing.status}
        onRetry={entry.retry}
        onImportStarted={sync.refetch}
      />
    );
  }

  if (screen === ENTRY_SCREEN.SYNC_ERROR) {
    return <EntryErrorScreen message={t('entry.syncReadFailed')} onRetry={entry.retry} />;
  }

  throw new Error(`EntryCard: no card for entry screen ${screen}`);
}

/**
 * Layout route for protected pages.
 *
 * Two auth guards decide whether there is a merchant at all (initialising, not
 * signed in). After them the ORDER of the screens between the merchant and the
 * app is one function, `components/entry/entryScreen.js`: question → setup →
 * price import → paywall → app, over one read (`hooks/useEntryStatus`), each
 * part failing closed into its own retry screen and every retry re-running
 * that read. This component renders whatever it names, and TabsHost (every
 * open tab, the inactive ones hidden but mounted) once it names APP.
 */
function ProtectedLayout() {
  const {
    isAuthenticated, isLoading, isInitialized, selectedStore, storesLoading, shopifySignInUrl,
    authReadError, retryAuth,
  } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Everything the entry screens decide on, in one read, once the session is
  // authenticated and its stores are loaded (the read names the selected
  // store). The backend says which parts apply: billing and sync only for a
  // Shopify store.
  const storeId = selectedStore?.id ?? null;
  const entry = useEntryStatus({ applies: isAuthenticated && !storesLoading, storeId });

  // The import, polled on from the payload the entry read seeded. A denied
  // merchant's price waits on it (`entryScreen` PRICE_IMPORT).
  const sync = useSyncStatus({
    applies: entry.checked && entry.sync !== null && !entry.sync.failed,
    storeId,
    accessDenied: !!entry.billing?.status?.needs_subscription,
  });

  // What the screens inside the app read (the Plans page, the plan-change
  // banner): the same answer that let the store in, and a re-read that keeps
  // the app mounted. Null where billing does not apply.
  const billingContext = useMemo(
    () => (entry.billing ? { status: entry.billing.status, refresh: entry.refreshBilling } : null),
    [entry.billing, entry.refreshBilling],
  );

  // Guard 1: Auth loading
  if (!isInitialized || isLoading) {
    return (
      <div className="app-loading">
        <div className="app-loading-content">
          <div>{t('session.validating')}</div>
          <div className="app-loading-subtitle">
            {extractTenantSlug() ?
              t('session.checkingTenantAccess', { tenant: extractTenantSlug() }) :
              t('session.checkingMainSiteAccess')
            }
          </div>
          {import.meta.env.DEV && (
            <div className="app-loading-debug">
              Session-based authentication
            </div>
          )}
        </div>
      </div>
    );
  }

  // Guard 2: The sign-in check got no answer — unknown is not signed out.
  if (authReadError) {
    return (
      <EntryErrorScreen message={t('entry.accountReadFailed')} error={authReadError} onRetry={retryAuth} />
    );
  }

  // Guard 3: Not authenticated, but this browser last signed in from a
  // Shopify store — through the door for it, never the login page.
  if (!isAuthenticated && shopifySignInUrl) {
    return <LeaveForShopifyDoor doorUrl={shopifySignInUrl} />;
  }

  // Guard 4: Not authenticated (or the session died during a read)
  if (!isAuthenticated || entry.sessionLost) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // The order itself.
  const screen = entryScreen({ entry, sync });

  if (screen !== ENTRY_SCREEN.APP) {
    return (
      <>
        <SaaSAnalyticsTracker recordsPage={false} />
        <EntryBackdrop>
          <EntryCard screen={screen} entry={entry} sync={sync} />
        </EntryBackdrop>
      </>
    );
  }

  // All guards passed — render the app. TabsHost replaces the router
  // <Outlet/>: it owns the rendering of every open tab (active tab
  // visible, inactive tabs hidden but mounted).
  return (
    <ErrorBoundary>
      <AgGridProvider modules={AG_GRID_MODULES} licenseKey={AG_LICENSE_KEY}>
       <BillingStatusContext.Provider value={billingContext}>
        <TabsProvider>
         <SaaSAnalyticsTracker />
         <CloseTabsOnStoreChange />
         {/* The dev server publishes no /version.json. */}
         {import.meta.env.PROD && <ReloadOnNewVersion />}
         <AppToast />
         <AppLayout content={<TabsHost />} />
        </TabsProvider>
       </BillingStatusContext.Provider>
      </AgGridProvider>
    </ErrorBoundary>
  );
}

/**
 * Root layout — provides AuthProvider to all routes. Must be inside
 * the router because AuthProvider uses useNavigate().
 */
function RootLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

// Build the tabbed-shell children dynamically from the registry. Each
// REGISTERED_ROUTES entry becomes either an index route (for '/') or a
// path route (for everything else). StaffGate wrapping is already baked
// into the registry's elements, so no nested guard route is needed here.
function toChildRoute({ path, element }) {
  if (path === '/') return { index: true, element };
  return { path: path.slice(1), element };
}

/**
 * Route configuration for createBrowserRouter.
 *
 * Structure:
 * - Root layout — AuthProvider (needs router context)
 *   - Public routes (login, setup, forgot/reset password, confirm email) — no auth required
 *   - Protected layout route — auth guard + AppLayout + TabsHost
 *     - One child route per REGISTERED_ROUTES entry (single source of truth)
 *     - Catch-all redirect to home
 */
export const appRoutes = [
  {
    element: <RootLayout />,
    children: [
      // Public routes (no authentication required)
      { path: '/login', element: <Login /> },
      { path: '/setup-account/:uid/:token', element: <SetupAccount /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/reset-password/:uid/:token', element: <ResetPassword /> },
      { path: '/confirm-email/:uid/:token', element: <ConfirmEmail /> },

      // Protected routes (authentication required) — fed by routeRegistry
      {
        element: <ProtectedLayout />,
        children: [
          ...REGISTERED_ROUTES.map(toChildRoute),
          // Catch-all redirect
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
];
